<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { evaluateSLAStatus } from '@chatwoot/utils';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
});

const REFRESH_INTERVAL = 60000;
const PIE_INTERVAL = 10000;

const timer = ref(null);
const pieTimer = ref(null);
const nowSeconds = ref(Math.floor(Date.now() / 1000));
const slaStatus = ref({
  threshold: null,
  isSlaMissed: false,
  type: null,
  icon: null,
});

// TODO: Remove this once we update the helper from utils
// https://github.com/chatwoot/utils/blob/main/src/sla.ts#L73
const convertObjectCamelCaseToSnakeCase = object => {
  return Object.keys(object).reduce((acc, key) => {
    acc[key.replace(/([A-Z])/g, '_$1').toLowerCase()] = object[key];
    return acc;
  }, {});
};

const appliedSLA = computed(() => props.conversation?.appliedSla);
const isSlaMissed = computed(() => slaStatus.value?.isSlaMissed);

const hasSlaThreshold = computed(() => {
  return slaStatus.value?.threshold && appliedSLA.value?.id;
});

const slaStatusText = computed(() => slaStatus.value?.type?.toUpperCase());

const updateSlaStatus = () => {
  slaStatus.value = evaluateSLAStatus({
    appliedSla: convertObjectCamelCaseToSnakeCase(appliedSLA.value || {}),
    chat: props.conversation,
  });
};

// Pie clock: determines the active SLA phase and the correct start point
const activeSlaPhase = computed(() => {
  const applied = appliedSLA.value;
  const chat = props.conversation;
  if (!applied) return null;

  // FRT: agent hasn't replied yet
  if (!chat.firstReplyCreatedAt && applied.slaFirstResponseTimeThreshold) {
    return {
      start: chat.createdAt,
      target: applied.slaFirstResponseTimeThreshold,
    };
  }
  // NRT: agent replied, customer sent a new message and is waiting
  if (chat.waitingSince && applied.slaNextResponseTimeThreshold) {
    return {
      start: chat.waitingSince,
      target: applied.slaNextResponseTimeThreshold,
    };
  }
  // RT: waiting for resolution
  if (applied.slaResolutionTimeThreshold) {
    return {
      start: chat.createdAt,
      target: applied.slaResolutionTimeThreshold,
    };
  }
  return null;
});

const slaPercent = computed(() => {
  const phase = activeSlaPhase.value;
  if (!phase || !phase.start || !phase.target) return 0;
  const elapsed = Math.max(0, nowSeconds.value - phase.start);
  return Math.min(1, elapsed / phase.target);
});

const slaTextStyles = computed(() => {
  if (isSlaMissed.value || slaPercent.value >= 1) return 'text-n-ruby-11';
  if (slaPercent.value >= 0.75) return 'text-orange-500';
  return 'text-n-amber-11';
});

const piePath = computed(() => {
  const p = slaPercent.value;
  const cx = 6;
  const cy = 6;
  const r = 5;
  const angle = p * 2 * Math.PI;
  const x = cx + r * Math.sin(angle);
  const y = cy - r * Math.cos(angle);
  const largeArc = p > 0.5 ? 1 : 0;
  return `M ${cx} ${cy} L ${cx} ${cy - r} A ${r} ${r} 0 ${largeArc} 1 ${x.toFixed(3)} ${y.toFixed(3)} Z`;
});

const createTimer = () => {
  timer.value = setTimeout(() => {
    updateSlaStatus();
    createTimer();
  }, REFRESH_INTERVAL);
};

const createPieTimer = () => {
  pieTimer.value = setTimeout(() => {
    nowSeconds.value = Math.floor(Date.now() / 1000);
    createPieTimer();
  }, PIE_INTERVAL);
};

watch(() => props.conversation, updateSlaStatus);

onMounted(() => {
  updateSlaStatus();
  createTimer();
  createPieTimer();
});

onUnmounted(() => {
  if (timer.value) clearTimeout(timer.value);
  if (pieTimer.value) clearTimeout(pieTimer.value);
});

defineExpose({
  hasSlaThreshold,
});
</script>

<template>
  <div
    class="relative flex items-center h-5 border rounded min-w-fit border-n-strong"
  >
    <div class="flex items-center w-full gap-1 truncate px-1.5">
      <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        class="flex-shrink-0"
        :class="slaTextStyles"
      >
        <circle cx="6" cy="6" r="5" fill="currentColor" opacity="0.25" />
        <path
          v-if="slaPercent > 0 && slaPercent < 1"
          :d="piePath"
          fill="currentColor"
        />
        <circle
          v-else-if="slaPercent >= 1"
          cx="6"
          cy="6"
          r="5"
          fill="currentColor"
        />
      </svg>
      <span class="text-xs font-medium" :class="slaTextStyles">
        {{
          slaStatusText
            ? `${slaStatusText}: ${slaStatus.threshold}`
            : slaStatus.threshold
        }}
      </span>
    </div>
  </div>
</template>
